import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { buildYouVersionLink } from '../utils/bibleReferences';
import { useBibleRefViewer } from './BibleRefViewer';

// Mapeo de book_id largo (usado en planes) → book_id corto (usado por audio)
const LONG_TO_SHORT_BOOK = {
  genesis: 'gen', exodo: 'exo', levitico: 'lev', numeros: 'num', deuteronomio: 'deut',
  josue: 'jos', jueces: 'jue', rut: 'rut', '1samuel': '1sa', '2samuel': '2sa',
  '1reyes': '1re', '2reyes': '2re', '1cronicas': '1cr', '2cronicas': '2cr', esdras: 'esd',
  nehemias: 'neh', ester: 'est', job: 'job', salmos: 'sal', proverbios: 'pro',
  eclesiastes: 'ecl', cantares: 'cant', isaias: 'isa', jeremias: 'jer', lamentaciones: 'lam',
  ezequiel: 'eze', daniel: 'dan', oseas: 'ose', joel: 'joel', amos: 'amo',
  abdias: 'abd', jonas: 'jon', miqueas: 'miq', nahum: 'nah', habacuc: 'hab',
  sofonias: 'sof', hageo: 'hag', zacarias: 'zac', malaquias: 'mal',
  mateo: 'mat', marcos: 'mar', lucas: 'luc', juan: 'juan', hechos: 'hech',
  romanos: 'rom', '1corintios': '1cor', '2corintios': '2cor', galatas: 'gal', efesios: 'efe',
  filipenses: 'fil', colosenses: 'col', '1tesalonicenses': '1tes', '2tesalonicenses': '2tes',
  '1timoteo': '1tim', '2timoteo': '2tim', tito: 'tit', filemon: 'file', hebreos: 'heb',
  santiago: 'sant', '1pedro': '1ped', '2pedro': '2ped', '1juan': '1juan', '2juan': '2juan',
  '3juan': '3juan', judas: 'judas', apocalipsis: 'apoc'
};
const AUDIO_BASE = 'https://www.wordproaudio.net/bibles/app/audio/6';
const BOOK_AUDIO_NUM = {
  gen:1,exo:2,lev:3,num:4,deut:5,jos:6,jue:7,rut:8,'1sa':9,'2sa':10,
  '1re':11,'2re':12,'1cr':13,'2cr':14,esd:15,neh:16,est:17,job:18,sal:19,pro:20,
  ecl:21,cant:22,isa:23,jer:24,lam:25,eze:26,dan:27,ose:28,joel:29,amo:30,
  abd:31,jon:32,miq:33,nah:34,hab:35,sof:36,hag:37,zac:38,mal:39,
  mat:40,mar:41,luc:42,juan:43,hech:44,rom:45,'1cor':46,'2cor':47,gal:48,efe:49,
  fil:50,col:51,'1tes':52,'2tes':53,'1tim':54,'2tim':55,tit:56,file:57,heb:58,sant:59,
  '1ped':60,'2ped':61,'1juan':62,'2juan':63,'3juan':64,judas:65,apoc:66
};

const getAudioUrlForPlan = (bookId, chapter) => {
  const short = LONG_TO_SHORT_BOOK[bookId] || bookId;
  const num = BOOK_AUDIO_NUM[short];
  if (!num) return null;
  // Proxy via backend para evitar bloqueos de service worker / cross-origin en mobile (mismo origen)
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
  return `${origin}/api/bible/audio-proxy/${num}/${chapter}.mp3`;
};

// Preguntas genéricas de reflexión (fuera del componente para evitar recreación)
const REFLECTION_QUESTIONS = [
  {
    id: 1,
    question: "¿Qué enseñanza te deja este capítulo?",
    placeholder: "Reflexiona sobre lo que aprendiste de esta lectura..."
  },
  {
    id: 2,
    question: "¿Cómo puedes aplicar esto a tu vida?",
    placeholder: "Piensa en cómo esta enseñanza impacta tu vida diaria..."
  }
];

// Campo de reflexión individual - Componente aislado para evitar re-renders del teclado
const ReflectionField = React.memo(({ 
  questionNumber,
  question, 
  placeholder, 
  value, 
  onChange 
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [charCount, setCharCount] = useState(value.length);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    setCharCount(newValue.length);
    onChange(newValue);
  };

  return (
    <div>
      <label className="block text-gray-700 font-semibold mb-2">
        {questionNumber}. {question} *
      </label>
      <textarea
        value={localValue}
        onChange={handleChange}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none"
        rows="4"
        placeholder={placeholder}
        autoComplete="off"
      />
      <p className="text-sm text-gray-500 mt-1">
        {charCount}/20 caracteres mínimo
      </p>
    </div>
  );
});

ReflectionField.displayName = 'ReflectionField';

// Modal de Reflexión - Componente separado optimizado para móvil
const ReflectionModal = React.memo(({ 
  isOpen,
  reflections, 
  onReflectionChange, 
  onSubmit, 
  onClose 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          💭 Reflexiona sobre tu lectura
        </h2>
        <p className="text-gray-600 mb-6">
          Antes de marcar como completado, toma un momento para reflexionar
        </p>

        <div className="space-y-6">
          <ReflectionField
            questionNumber={1}
            question={REFLECTION_QUESTIONS[0].question}
            placeholder={REFLECTION_QUESTIONS[0].placeholder}
            value={reflections.answer1}
            onChange={(value) => onReflectionChange('answer1', value)}
          />

          <ReflectionField
            questionNumber={2}
            question={REFLECTION_QUESTIONS[1].question}
            placeholder={REFLECTION_QUESTIONS[1].placeholder}
            value={reflections.answer2}
            onChange={(value) => onReflectionChange('answer2', value)}
          />
        </div>

        <div className="flex gap-4 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all"
          >
            Cancelar
          </button>
          <button
            onClick={onSubmit}
            disabled={reflections.answer1.length < 20 || reflections.answer2.length < 20}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Guardar y Completar
          </button>
        </div>
      </div>
    </div>
  );
});

ReflectionModal.displayName = 'ReflectionModal';

const BibliaPlanes = () => {
  const API = `${window.location.origin}/api`;
  const bibleViewer = useBibleRefViewer();
  const [plans, setPlans] = useState([]);
  const [activeTab, setActiveTab] = useState('lectura');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showReadingModal, setShowReadingModal] = useState(false);
  const [readingContent, setReadingContent] = useState(null);
  const [loadingReading, setLoadingReading] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [audioPlaylist, setAudioPlaylist] = useState([]); // [{bookId, bookName, chapter, url}]
  const [audioIdx, setAudioIdx] = useState(0);
  const audioRef = useRef(null);
  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const [currentDay, setCurrentDay] = useState(null);
  const [reflections, setReflections] = useState({
    answer1: '',
    answer2: ''
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/bible/reading-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Error al cargar planes de lectura');
    } finally {
      setLoading(false);
    }
  };

  const startPlan = async (planId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/bible/reading-plans/${planId}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('¡Plan iniciado! Empieza a leer hoy');
      loadPlans();
    } catch (error) {
      console.error('Error starting plan:', error);
      toast.error('Error al iniciar plan');
    }
  };

  const viewPlanDetails = async (planId) => {
    const plan = plans.find(p => p.plan_id === planId);
    setSelectedPlan(plan);
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/bible/reading-plans/${planId}/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedule(response.data.schedule);
    } catch (error) {
      console.error('Error loading schedule:', error);
    }
  };

  const loadChapterForReading = async (items) => {
    // items: array de {bookId, chapter}
    try {
      setLoadingReading(true);

      const chaptersData = [];
      for (const it of items) {
        if (!it.bookId || !it.chapter) continue;
        // El interceptor global en App.js inyecta el token automáticamente.
        const response = await axios.get(`${API}/bible/${it.bookId}/${it.chapter}`);
        chaptersData.push(response.data.data);
      }

      if (chaptersData.length === 0) {
        toast.warning('No se pudieron cargar los capítulos de este día');
        return;
      }
      setReadingContent(chaptersData);
      setShowReadingModal(true);
    } catch (error) {
      console.error('Error loading chapter:', error);
      toast.error('Error al cargar capítulo');
    } finally {
      setLoadingReading(false);
    }
  };

  const handleReadChapter = (day) => {
    // Construir lista de {bookId, chapter} desde múltiples formatos posibles
    // de schedule (legacy + nuevos planes multi-libro YouVersion).
    const items = [];

    // 1) Formato nuevo / multi-libro: day.readings = [{book_id|api_id|book, chapter|chapters}]
    if (Array.isArray(day.readings) && day.readings.length > 0) {
      for (const r of day.readings) {
        const bid = r.api_id || r.book_id || r.book; // soporta los 3 nombres
        if (!bid) continue;
        if (Array.isArray(r.chapters)) {
          r.chapters.forEach(c => items.push({ bookId: bid, chapter: c }));
        } else if (r.chapter) {
          items.push({ bookId: bid, chapter: r.chapter });
        }
      }
    }

    // 2) Formato clásico: day.api_id + day.chapters
    if (items.length === 0 && day.api_id && Array.isArray(day.chapters)) {
      day.chapters.forEach(c => items.push({ bookId: day.api_id, chapter: c }));
    }

    // 3) Fallback histórico: day.book_id + day.chapters
    if (items.length === 0 && day.book_id && Array.isArray(day.chapters)) {
      day.chapters.forEach(c => items.push({ bookId: day.book_id, chapter: c }));
    }

    if (items.length === 0) {
      toast.warning('Este plan no tiene lectura configurada');
      return;
    }
    loadChapterForReading(items);
  };

  const handleListenChapter = (day) => {
    setCurrentDay(day.day);
    // Preferir el array `readings` (multi-libro) si existe, sino usar book_id + chapters
    let playlist = [];
    if (Array.isArray(day.readings) && day.readings.length > 0) {
      playlist = day.readings.map(r => ({
        bookId: r.book_id || r.api_id,
        bookName: r.book_name,
        chapter: r.chapter,
        url: getAudioUrlForPlan(r.book_id || r.api_id, r.chapter)
      }));
    } else if (day.chapters && day.chapters.length > 0) {
      const bid = day.book_id || day.api_id;
      playlist = day.chapters.map(c => ({
        bookId: bid,
        bookName: day.book_name || bid,
        chapter: c,
        url: getAudioUrlForPlan(bid, c)
      }));
    }

    playlist = playlist.filter(p => p.url);
    if (playlist.length === 0) {
      toast.warning('No se encontró audio para este día');
      return;
    }
    setAudioPlaylist(playlist);
    setAudioIdx(0);
    setShowAudioModal(true);

    // Mobile fix: Safari iOS / Chrome Android requieren gesto explícito.
    // Disparamos play() inmediatamente después del gesto del click (se preserva user-activation).
    // Usamos setTimeout para esperar a que el <audio> esté montado en el DOM.
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.load();
        const p = audioRef.current.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => {
            // Autoplay bloqueado: el usuario deberá tocar el botón play del reproductor nativo (que ya está enabled al cargar metadata)
          });
        }
      }
    }, 150);
  };

  // Intentar reproducir al cambiar de pista (después del gesto inicial el navegador permite play automático)
  useEffect(() => {
    if (!showAudioModal || !audioRef.current || audioPlaylist.length === 0) return;
    const el = audioRef.current;
    // Cuando cambia el src (via key/playlist[audioIdx]), forzar load y play
    const tryPlay = () => {
      try {
        el.load();
        const p = el.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (_) { /* noop */ }
    };
    tryPlay();
  }, [audioIdx, showAudioModal, audioPlaylist.length]);

  const markDayAsListened = async (dayNumber) => {
    try {
      const token = localStorage.getItem('access_token');
      const resp = await axios.post(
        `${API}/bible/reading-plans/${selectedPlan.plan_id}/mark-listened`,
        { day_number: dayNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Actualizar estado local inmediatamente
      if (resp.data.listened_days) {
        setSelectedPlan(prev => prev ? { ...prev, listened_days: resp.data.listened_days } : prev);
      }
      loadPlans();
    } catch (error) {
      console.error('Error marking as listened:', error);
    }
  };

  const openReflectionModal = (day) => {
    setCurrentDay(day);
    setReflections({ answer1: '', answer2: '' });
    setShowReflectionModal(true);
  };

  const submitReflections = async () => {
    // Validar mínimo de caracteres
    if (reflections.answer1.trim().length < 20) {
      toast.error('La primera reflexión debe tener al menos 20 caracteres');
      return;
    }
    if (reflections.answer2.trim().length < 20) {
      toast.error('La segunda reflexión debe tener al menos 20 caracteres');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/bible/reading-plans/${selectedPlan.plan_id}/complete-day`,
        {
          day_number: currentDay,
          reflections: {
            question1: REFLECTION_QUESTIONS[0].question,
            answer1: reflections.answer1,
            question2: REFLECTION_QUESTIONS[1].question,
            answer2: reflections.answer2
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      
      if (data.plan_completed && data.denarios_awarded > 0) {
        toast.success(`🎉 ${data.message}`, {
          autoClose: 5000,
          style: {
            background: 'linear-gradient(to right, #6366f1, #a855f7)',
            color: 'white',
            fontSize: '16px',
            fontWeight: 'bold'
          }
        });
      } else {
        toast.success(`¡Día ${currentDay} completado! 📖`);
      }
      
      setShowReflectionModal(false);
      setReflections({ answer1: '', answer2: '' });
      loadPlans();
      if (selectedPlan) {
        viewPlanDetails(selectedPlan.plan_id);
      }
    } catch (error) {
      console.error('Error completing day:', error);
      toast.error(error.response?.data?.detail || 'Error al marcar día como completado');
    }
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Fácil': 'bg-green-100 text-green-800',
      'Intermedio': 'bg-yellow-100 text-yellow-800',
      'Avanzado': 'bg-red-100 text-red-800'
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-800';
  };

  const handleReflectionChange = useCallback((field, value) => {
    setReflections(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Cargando planes...</div>
      </div>
    );
  }

  // Modal de Lectura
  const ReadingModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b-2 border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">📖 Lectura del Día</h2>
          <button
            onClick={() => {
              setShowReadingModal(false);
              setReadingContent(null);
            }}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>
        
        <div className="p-6">
          {loadingReading ? (
            <div className="text-center py-12">
              <div className="text-xl text-gray-600">Cargando capítulo...</div>
            </div>
          ) : readingContent ? (
            <div className="space-y-8">
              {readingContent.map((chapterData, idx) => (
                <div key={idx}>
                  <h3 className="text-xl font-bold text-indigo-600 mb-4">
                    {chapterData.book} Capítulo {chapterData.chapter}
                  </h3>
                  <div className="space-y-3">
                    {chapterData.verses.map((verse) => (
                      <div key={verse.verse} className="flex">
                        <span className="flex-shrink-0 w-10 text-indigo-600 font-semibold text-sm">
                          {verse.verse}
                        </span>
                        <p className="flex-1 text-gray-700 leading-relaxed">
                          {verse.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No se pudo cargar el contenido</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (selectedPlan) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8">
        <button
          onClick={() => setSelectedPlan(null)}
          className="mb-4 text-indigo-600 hover:text-indigo-800 font-semibold text-sm sm:text-base"
        >
          ← Volver a planes
        </button>
        
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">{selectedPlan.name}</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4">{selectedPlan.description}</p>
          
          <div className="flex flex-wrap gap-2 sm:gap-3 mb-6">
            <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${getDifficultyColor(selectedPlan.difficulty)}`}>
              {selectedPlan.difficulty}
            </span>
            <span className="px-3 py-1 rounded-full text-xs sm:text-sm font-semibold bg-blue-100 text-blue-800">
              {selectedPlan.duration_days} días
            </span>
            <span className="px-3 py-1 rounded-full text-xs sm:text-sm font-semibold bg-yellow-100 text-yellow-800">
              🪙 {selectedPlan.denarios_reward || 0} denarios
            </span>
          </div>

          {/* Barra de progreso */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <span className="text-xs sm:text-sm font-semibold text-gray-700">Progreso</span>
              <span className="text-xs sm:text-sm font-semibold text-indigo-600">
                {selectedPlan.progress_percentage}% ({selectedPlan.progress.length}/{selectedPlan.duration_days} días)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-indigo-600 to-purple-600 h-3 rounded-full transition-all"
                style={{ width: `${selectedPlan.progress_percentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Cronograma de días - Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {schedule.map((daySchedule) => {
            const dayNum = daySchedule.day;
            const isCompleted = selectedPlan.progress.includes(dayNum);
            const listenedDays = selectedPlan.listened_days || [];
            const isListened = listenedDays.includes(dayNum) && !isCompleted;
            const isCurrent = dayNum === selectedPlan.progress.length + 1;
            
            return (
              <div
                key={dayNum}
                className={`border-2 rounded-xl p-3 sm:p-4 transition-all ${
                  isCompleted
                    ? 'border-green-500 bg-green-50'
                    : isListened
                    ? 'border-amber-500 bg-amber-50'
                    : isCurrent
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 bg-white'
                }`}
                data-testid={`day-card-${dayNum}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-base sm:text-lg">
                    {daySchedule.title ? (
                      <>Día {dayNum}: <span className="text-amber-700">{daySchedule.title}</span></>
                    ) : (
                      <>Día {dayNum}</>
                    )}
                  </span>
                  {isCompleted && <span className="text-xl sm:text-2xl" title="Completado con reflexión">✅</span>}
                  {isListened && <span className="text-xl sm:text-2xl" title="Ya escuchaste. Falta la reflexión para ganar denarios.">🎧</span>}
                  {isCurrent && !isCompleted && !isListened && <span className="text-xl sm:text-2xl">📖</span>}
                </div>
                
                {daySchedule.reading && (
                  <div className="text-xs sm:text-sm text-gray-600 mb-2 font-medium">
                    {daySchedule.reading}
                  </div>
                )}

                {/* Reflexión del plan temático generada por AI */}
                {daySchedule.is_tematico && daySchedule.reflection && (
                  <div className="text-xs text-gray-700 bg-amber-50 border border-amber-100 rounded p-2 mb-3 italic leading-relaxed">
                    {daySchedule.reflection}
                  </div>
                )}

                {isListened && (
                  <div className="mb-2 text-[11px] sm:text-xs text-amber-700 bg-amber-100 rounded px-2 py-1 font-medium">
                    🎧 Ya escuchaste · Falta reflexión para ganar denarios
                  </div>
                )}
                
                {!isCompleted && (
                  <div className="space-y-2">
                    {/* Para planes temáticos: abrir el modal inline (BibleRefViewer)
                       con el verso de YouVersion, en vez de salir a la app externa. */}
                    {daySchedule.is_tematico ? (
                      daySchedule.reference_usfm ? (
                        <div className="space-y-1.5">
                          <button
                            type="button"
                            onClick={() => bibleViewer.open(
                              daySchedule.reference_usfm,
                              daySchedule.reference_label || daySchedule.reading || daySchedule.reference_usfm
                            )}
                            className="block w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-amber-600 text-white rounded-lg text-xs sm:text-sm font-semibold hover:bg-amber-700 transition-all text-center"
                            data-testid={`read-yv-day-${dayNum}`}
                          >
                            <i className="fas fa-book-open mr-1" /> Leer cita bíblica
                          </button>
                          <a
                            href={buildYouVersionLink(daySchedule.reference_usfm)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full text-center text-[10px] text-gray-400 hover:text-amber-600 transition"
                            data-testid={`read-yv-external-day-${dayNum}`}
                          >
                            <i className="fas fa-external-link-alt mr-1" /> Abrir en YouVersion
                          </a>
                        </div>
                      ) : (
                        <div className="text-center text-xs text-gray-500 py-1">Sin enlace disponible</div>
                      )
                    ) : (selectedPlan.tipo === 'audio') ? (
                      <button
                        onClick={() => handleListenChapter(daySchedule)}
                        className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 text-white rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                          isListened ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'
                        }`}
                        data-testid={`listen-day-${dayNum}`}
                      >
                        <i className="fas fa-headphones mr-1" />{isListened ? 'Volver a Escuchar' : 'Escuchar Capítulo'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReadChapter(daySchedule)}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-semibold hover:bg-blue-700 transition-all"
                        data-testid={`read-day-${dayNum}`}
                      >
                        📖 Leer Capítulo
                      </button>
                    )}

                    {/* Botón Marcar Completado */}
                    <button
                      onClick={() => openReflectionModal(dayNum)}
                      className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 text-white rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                        isListened ? 'bg-green-600 hover:bg-green-700 animate-pulse' : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                    >
                      ✅ {isListened ? 'Reflexionar y Completar' : 'Marcar Completado'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Modales */}
        {showReadingModal && <ReadingModal />}
        {showAudioModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="fas fa-headphones text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-lg">Escuchar Capítulo</h2>
                    <p className="text-white/80 text-xs">
                      {audioPlaylist[audioIdx]
                        ? `${audioPlaylist[audioIdx].bookName} ${audioPlaylist[audioIdx].chapter}`
                        : ''}
                      {audioPlaylist.length > 1 ? ` · ${audioIdx + 1}/${audioPlaylist.length}` : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAudioModal(false);
                    if (audioRef.current) audioRef.current.pause();
                    setAudioPlaylist([]);
                    setAudioIdx(0);
                  }}
                  className="text-white hover:text-gray-200 text-2xl w-8 h-8 flex items-center justify-center"
                  data-testid="close-audio-modal"
                >×</button>
              </div>
              <div className="p-6 space-y-4">
                {audioPlaylist[audioIdx] && (
                  <>
                    <audio
                      ref={audioRef}
                      key={audioPlaylist[audioIdx].url}
                      src={audioPlaylist[audioIdx].url}
                      controls
                      playsInline
                      preload="auto"
                      onEnded={() => {
                        if (audioIdx < audioPlaylist.length - 1) {
                          setAudioIdx(audioIdx + 1);
                        } else {
                          toast.success('🎧 Terminaste el audio del día. Ahora podés marcarlo como completado.');
                          if (currentDay) markDayAsListened(currentDay);
                        }
                      }}
                      onError={() => toast.error('No se pudo cargar el audio. Verificá tu conexión.')}
                      className="w-full"
                      data-testid="audio-player"
                    />
                    {audioPlaylist.length > 1 && (
                      <div className="flex items-center justify-between gap-2">
                        <button
                          onClick={() => setAudioIdx(Math.max(0, audioIdx - 1))}
                          disabled={audioIdx === 0}
                          className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm font-semibold hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid="audio-prev"
                        >
                          <i className="fas fa-backward mr-1" />Anterior
                        </button>
                        <div className="flex-1 text-center text-xs text-gray-500">
                          Pista {audioIdx + 1} de {audioPlaylist.length}
                        </div>
                        <button
                          onClick={() => setAudioIdx(Math.min(audioPlaylist.length - 1, audioIdx + 1))}
                          disabled={audioIdx === audioPlaylist.length - 1}
                          className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm font-semibold hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid="audio-next"
                        >
                          Siguiente<i className="fas fa-forward ml-1" />
                        </button>
                      </div>
                    )}
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-700">
                      <i className="fas fa-info-circle mr-1" />
                      Cuando termines de escuchar, cerrá este reproductor y marcá el día como completado.
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        <ReflectionModal 
          isOpen={showReflectionModal}
          reflections={reflections}
          onReflectionChange={handleReflectionChange}
          onSubmit={submitReflections}
          onClose={() => {
            setShowReflectionModal(false);
            setReflections({ answer1: '', answer2: '' });
          }}
        />
      </div>
    );
  }

  const TABS = [
    { key: 'lectura', label: 'Lectura', icon: 'fas fa-book-open' },
    { key: 'audio', label: 'Escuchar', icon: 'fas fa-headphones' }
  ];

  const filteredPlans = plans.filter(p => {
    const t = p.tipo || 'lectura';
    return t === activeTab || (activeTab === 'lectura' && t !== 'audio');
  });

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8">
      <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-gray-800 flex items-center">
        <span className="text-3xl sm:text-4xl mr-3"><i className="fas fa-book-bible text-indigo-600"></i></span>
        Planes de la Biblia
      </h2>
      
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6" data-testid="planes-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 sm:px-6 py-3 text-sm sm:text-base font-semibold border-b-2 transition-all ${
              activeTab === tab.key 
                ? 'border-indigo-600 text-indigo-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {filteredPlans.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">
            <i className={`${TABS.find(t => t.key === activeTab)?.icon || 'fas fa-book'} text-gray-300`}></i>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No hay planes de {TABS.find(t => t.key === activeTab)?.label.toLowerCase()} disponibles
          </h3>
          <p className="text-gray-500">
            Los administradores pueden crear nuevos planes desde el panel de gestión
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredPlans.map(plan => (
            <div
              key={plan.plan_id}
              className="border-2 border-gray-200 rounded-xl p-4 sm:p-6 hover:border-indigo-500 hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 pr-2">{plan.name}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(plan.difficulty)} flex-shrink-0`}>
                  {plan.difficulty}
                </span>
              </div>
              
              <p className="text-gray-600 mb-3 sm:mb-4 text-xs sm:text-sm">{plan.description}</p>
              
              <div className="flex items-center gap-2 mb-3 sm:mb-4 text-xs sm:text-sm text-gray-500 flex-wrap">
                <span>📖 {plan.category}</span>
                <span>•</span>
                <span>⏱️ {plan.duration_days} días</span>
              </div>
              
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-700 font-medium">🪙 Recompensa:</span>
                  <span className="text-base sm:text-lg font-bold text-indigo-600">{plan.denarios_reward || 0} denarios</span>
                </div>
              </div>

              {plan.started ? (
                <div>
                  <div className="mb-3">
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2 rounded-full"
                        style={{ width: `${plan.progress_percentage}%` }}
                      ></div>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {plan.progress_percentage}% completado ({plan.progress.length}/{plan.duration_days} días)
                    </p>
                  </div>
                  <button
                    onClick={() => viewPlanDetails(plan.plan_id)}
                    className="w-full px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm sm:text-base font-semibold hover:bg-indigo-700 transition-all"
                  >
                    Continuar Plan
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => startPlan(plan.plan_id)}
                  className="w-full px-3 sm:px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-sm sm:text-base font-semibold hover:shadow-lg transition-all"
                >
                  Iniciar Plan
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BibliaPlanes;
